// Webhooks (plan step 5.2; D88): Stripe → Hookdeck → API health without opening Hookdeck. The
// last day of Stripe events as the API handled them, Hookdeck's failed deliveries (joined to
// the Stripe event they carried) and open issues, and a retry per failed delivery, with a
// reason, in the admin audit log. Metadata only: no event body or header ever reaches here.
// Console D (APS-412, admin-d.md): a strip of four, one section per source, Hookdeck's states as
// one line in its section, the retry in a form window under its table.
import { ErrorState as ErrorLine, PageHead, Section, SectionHead, Strip } from '@apsio/ui'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { retryWebhook, useAdminMutation, type Webhooks, webhooksQuery } from '../api/queries'
import { ActionForm } from '../components/ActionForm'
import { DataTable, IdText, NotConfigured, StatusWord } from '../components/kit'
import { Empty, ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'

type HookdeckEvent = Webhooks['hookdeck']['events'][number]

/** Hookdeck's issue types in the admin's words; any other reads as Hookdeck sends it. */
const ISSUE_TYPE: Record<string, MessageId> = {
  delivery: 'webhooks.issueType.delivery',
  transformation: 'webhooks.issueType.transformation',
  backpressure: 'webhooks.issueType.backpressure',
}

function Summary({ data }: { data: Webhooks }) {
  const { t, date, number } = useI18n()
  return (
    <Strip
      items={[
        { dt: t('webhooks.received'), dd: number(data.received) },
        { dt: t('webhooks.processed'), dd: number(data.processed) },
        // The page's one accent: what failed, when anything did.
        { dt: t('webhooks.failed'), dd: number(data.failed), cur: data.failed > 0 },
        // The time as the figure and its day under it: a day and a time never fit a cell.
        {
          dt: t('webhooks.lastProcessed'),
          dd: data.last_processed_at
            ? date(data.last_processed_at, { hour: '2-digit', minute: '2-digit' })
            : t('common.none'),
          d: data.last_processed_at ? date(data.last_processed_at) : undefined,
        },
      ]}
    />
  )
}

function FailedDeliveries({ data }: { data: Webhooks }) {
  const { t, dateTime } = useI18n()
  const [retrying, setRetrying] = useState<HookdeckEvent | null>(null)
  const retry = useAdminMutation(retryWebhook, [['webhooks']])
  if (data.hookdeck.events.length === 0) return <Empty title={t('webhooks.noFailed')} />
  return (
    <>
      <DataTable label={t('webhooks.failedTable')} minWidth={880}>
        <thead>
          <tr>
            <th scope="col" className="f">
              {t('webhooks.col.hookdeck')}
            </th>
            <th scope="col">{t('webhooks.col.status')}</th>
            <th scope="col">{t('webhooks.col.stripe')}</th>
            <th scope="col" className="num">
              {t('webhooks.col.attempts')}
            </th>
            <th scope="col">{t('webhooks.col.response')}</th>
            <th scope="col">{t('webhooks.col.last')}</th>
            <th scope="col">{t('webhooks.col.next')}</th>
            <th scope="col" className="last">
              <span className="visually-hidden">{t('webhooks.col.action')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {data.hookdeck.events.map((e) => (
            <tr key={e.id} className={retrying?.id === e.id ? 'cur' : undefined}>
              <td className="f">
                <IdText id={e.id} />
              </td>
              <td>
                <StatusWord kind="webhook" status={e.status} />
              </td>
              <td translate="no">
                {e.stripe_event_id ? (
                  <>
                    {e.stripe_type} · <IdText id={e.stripe_event_id} />
                  </>
                ) : (
                  t('common.none')
                )}
              </td>
              <td className="num">{e.attempts}</td>
              <td translate="no">{e.response_status ?? e.error_code ?? t('common.none')}</td>
              <td>{e.last_attempt_at ? dateTime(e.last_attempt_at) : t('common.none')}</td>
              <td>{e.next_attempt_at ? dateTime(e.next_attempt_at) : t('common.none')}</td>
              <td className="last">
                <button
                  type="button"
                  className="btn ghost sm"
                  aria-label={t('webhooks.retryTitle', { id: e.id })}
                  onClick={() => setRetrying(e)}
                >
                  {t('webhooks.retry')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </DataTable>
      {retrying ? (
        // The retry: Hookdeck delivers it again; the reason goes to the audit log.
        <div className="ops-win">
          <ActionForm
            key={retrying.id}
            open
            onClose={() => setRetrying(null)}
            label={t('webhooks.retryTitle', { id: retrying.id })}
            confirm={t('webhooks.retryConfirm')}
            onSubmit={(reason) => retry.mutateAsync({ eventId: retrying.id, reason })}
          />
        </div>
      ) : null}
    </>
  )
}

function Issues({ data }: { data: Webhooks }) {
  const { t, dateTime } = useI18n()
  if (data.hookdeck.issues.length === 0) return <Empty title={t('webhooks.noIssues')} />
  return (
    <DataTable label={t('webhooks.issuesTitle')} minWidth={640}>
      <thead>
        <tr>
          <th scope="col" className="f">
            {t('webhooks.col.issue')}
          </th>
          <th scope="col">{t('webhooks.col.type')}</th>
          <th scope="col">{t('webhooks.col.statuses')}</th>
          <th scope="col" className="last">
            {t('webhooks.col.opened')}
          </th>
        </tr>
      </thead>
      <tbody>
        {data.hookdeck.issues.map((i) => (
          <tr key={i.id}>
            <td className="f">
              <IdText id={i.id} />
            </td>
            <td>{ISSUE_TYPE[i.type] ? t(ISSUE_TYPE[i.type] as MessageId) : i.type}</td>
            <td translate="no">{i.response_statuses.join(', ') || t('common.none')}</td>
            <td className="last">{i.opened_at ? dateTime(i.opened_at) : t('common.none')}</td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  )
}

/** Hookdeck's own section: not set up and down are one line each; nothing else hides. */
function Hookdeck({ data, retry }: { data: Webhooks; retry: () => void }) {
  const { t } = useI18n()
  const { hookdeck } = data
  return (
    <Section
      title={t('webhooks.hookdeckTitle')}
      meta={
        hookdeck.open_issues === null
          ? undefined
          : t('webhooks.openIssuesMeta', { n: hookdeck.open_issues })
      }
    >
      {!hookdeck.configured ? (
        <NotConfigured missing={t('webhooks.notConfigured')} task={t('kit.ownerTask')} />
      ) : hookdeck.error ? (
        <ErrorLine
          message={t('webhooks.unavailable')}
          retry={retry}
          retryLabel={t('state.retry')}
        />
      ) : (
        <>
          <SectionHead minor title={t('webhooks.failedTitle')} />
          <FailedDeliveries data={data} />
          <SectionHead minor title={t('webhooks.issuesTitle')} />
          <Issues data={data} />
        </>
      )}
    </Section>
  )
}

function LocalFailures({ data }: { data: Webhooks }) {
  const { t, dateTime } = useI18n()
  if (data.failures.length === 0) return <Empty title={t('webhooks.noLocalFailures')} />
  return (
    <DataTable label={t('webhooks.localTable')} minWidth={640}>
      <thead>
        <tr>
          <th scope="col" className="f">
            {t('webhooks.col.stripe')}
          </th>
          <th scope="col">{t('webhooks.col.received')}</th>
          <th scope="col" className="last">
            {t('webhooks.col.error')}
          </th>
        </tr>
      </thead>
      <tbody>
        {data.failures.map((f) => (
          <tr key={f.stripe_event_id}>
            <td className="f" translate="no">
              {f.type} · <IdText id={f.stripe_event_id} />
            </td>
            <td>{dateTime(f.received_at)}</td>
            <td className="last wrap" translate="no">
              {f.error ?? t('common.none')}
            </td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  )
}

export function WebhooksPage() {
  const { t } = useI18n()
  const webhooks = useQuery(webhooksQuery)
  return (
    <main id="main" className="pg ops">
      <PageHead title={t('webhooks.title')} meta={t('webhooks.lede')} />
      {webhooks.isPending ? (
        <Loading />
      ) : webhooks.isError ? (
        <ErrorState error={webhooks.error} retry={() => webhooks.refetch()} />
      ) : (
        <>
          <Summary data={webhooks.data} />
          <Hookdeck data={webhooks.data} retry={() => webhooks.refetch()} />
          <Section title={t('webhooks.localTitle')}>
            <LocalFailures data={webhooks.data} />
          </Section>
        </>
      )}
    </main>
  )
}
